import { useCallback, useMemo } from 'react'
import {
  type AreaData,
  type ElasticFilter,
  type ElasticFiltersInput,
  type ElasticFiltersSuccess,
  HOUR,
  isNotEmpty,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import {
  toAreaAccountCounts,
  toFilterBoundingBox,
  useSearchAccountCounts,
  type AreaAccountCounts,
} from '../totalAccounts'
import { queryObjectToKey } from '../../search/findCachedDataForRegion'
import { type GetHookResponse } from '../../hooks'
import { useServerRequest } from '../../api'

export const useAreaMatchAccountCounts = (
  area: AreaData,
  enabled = true,
): GetHookResponse<AreaAccountCounts> => {
  const filters = useMemo(() => areaToElasticFilter(area), [area])
  return useSearchAccountCounts(filters, enabled)
}

export type FetchAreaAccountCounts = (area: AreaData) => Promise<AreaAccountCounts>

export const useFetchAreaAccountCounts = (): FetchAreaAccountCounts => {
  const serverRequest = useServerRequest<ElasticFiltersInput, ElasticFiltersSuccess>('/search/filters')
  const queryClient = useQueryClient()

  return useCallback(
    async (area: AreaData): Promise<AreaAccountCounts> => {
      const filters = areaToElasticFilter(area)
      const input: ElasticFiltersInput = { filters: { ...filters, boundingBox: toFilterBoundingBox(filters) } }
      const result = await queryClient.fetchQuery({
        queryKey: queryObjectToKey(input),
        staleTime: HOUR,
        queryFn: () => serverRequest(input),
      })
      return toAreaAccountCounts(result)
    },
    [queryClient, serverRequest]
  )
}

export const areaToElasticFilter = (area: AreaData): ElasticFilter => {
  const { filters } = area

  return {
    ...filters,
    coordinates: isNotEmpty(area.coordinates) ? area.coordinates : undefined,
  }
}
